'use client';

import { useMemo } from 'react';
import { useTranslations } from 'next-intl';
import {
  ChatCopilotEmptyStateLayout,
  CopilotEmptyGlow,
  type ChatSuggestionCategory,
} from '@terra/ui-design-system';
import {
  HelpCircleIcon,
  Target01Icon,
  FilePlusIcon,
  FileSearchIcon,
  Globe02Icon,
} from '@terra/ui-icons';
import type { TerraIcon } from '@terra/ui-icons';
import type { CoPilotInitialStateProps } from './types';

const CATEGORY_ICONS: Record<string, TerraIcon> = {
  createNewObjects: FilePlusIcon,
  attacking: Target01Icon,
  yourEnvironment: Globe02Icon,
  investigating: FileSearchIcon,
  terraPortal: HelpCircleIcon,
};

export function CoPilotInitialState({
  onPromptClick,
}: Pick<CoPilotInitialStateProps, 'onPromptClick'>) {
  const t = useTranslations('chat.copilot');
  const tCategories = useTranslations('chat.copilot.categories');
  const categories = useMemo<ChatSuggestionCategory[]>(
    () => [
      {
        id: 'attacking',
        title: tCategories('attacking.title'),
        pillLabel: tCategories('attacking.pillLabel'),
        Icon: CATEGORY_ICONS['attacking'],
        prompts: [
          tCategories('attacking.prompt0'),
          tCategories('attacking.prompt1'),
        ],
      },
      {
        id: 'investigating',
        title: tCategories('investigating.title'),
        pillLabel: tCategories('investigating.pillLabel'),
        Icon: CATEGORY_ICONS['investigating'],
        prompts: [
          tCategories('investigating.prompt0'),
          tCategories('investigating.prompt1'),
          tCategories('investigating.prompt2'),
        ],
      },
      {
        id: 'terraPortal',
        title: tCategories('terraPortal.title'),
        pillLabel: tCategories('terraPortal.pillLabel'),
        Icon: CATEGORY_ICONS['terraPortal'],
        prompts: [
          tCategories('terraPortal.prompt0'),
          tCategories('terraPortal.prompt1'),
          tCategories('terraPortal.prompt2'),
        ],
      },
      {
        id: 'createNewObjects',
        title: tCategories('createNewObjects.title'),
        pillLabel: tCategories('createNewObjects.pillLabel'),
        Icon: CATEGORY_ICONS['createNewObjects'],
        prompts: [
          tCategories('createNewObjects.prompt0'),
          tCategories('createNewObjects.prompt1'),
          tCategories('createNewObjects.prompt2'),
        ],
      },
      {
        id: 'yourEnvironment',
        title: tCategories('yourEnvironment.title'),
        pillLabel: tCategories('yourEnvironment.pillLabel'),
        Icon: CATEGORY_ICONS['yourEnvironment'],
        prompts: [
          tCategories('yourEnvironment.prompt0'),
          tCategories('yourEnvironment.prompt1'),
          tCategories('yourEnvironment.prompt2'),
        ],
      },
    ],
    [tCategories],
  );
  const categoryPillRows = useMemo(
    () =>
      [
        [0, 1, 2],
        [3, 4],
      ] as const,
    [],
  );

  return (
    <ChatCopilotEmptyStateLayout
      variant="hero"
      header={<CopilotEmptyGlow />}
      title={t('emptyTitle')}
      welcomeMessage={t('welcomeMessage')}
      categories={categories}
      closeCategoryLabel={t('closeCategory')}
      onPromptClick={onPromptClick}
      pillRowIndexes={categoryPillRows}
      showCategoryIconInPanel
    />
  );
}
